/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { toast } from "react-toastify";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { v4 as uuidv4 } from "uuid";
import { ContactApi } from "../../../../api/Contacts";
import { updateSavedSearch } from "../../../../api/SavedSearches";
import { CountedSavedSearchesAtom } from "../../../../atoms/SavedSearches";
import { TagsAtom } from "../../../../atoms/TagsAtom";
import { Flex } from "../../../../styles/general";
import { IOption } from "../../../../types/CommonTypes";
import {
  CountedSavedSearches,
  IRecipientTag,
  SavedSearch,
} from "../../../../types/SavedSearchTypes";
import { ITag } from "../../../../types/TagTypes";
import {
  recipientToTagQuery,
  tagQueryToRecipient,
} from "../../../../utils/TagQuery";
import { useModal } from "../../../Modal/Modal";
import StyledButton from "../../../StyledButton";
import TagSelectRecipient from "../../../TagSelect/TagSelectRecipient";
import {
  Bottom,
  Container,
  Header,
  HeaderTitle,
  SavedSearchModal,
  Wrapper,
} from "../SavedSearches";

const LogicButton = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 32px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid;
  border-color: #cccccc;
  color: #979797;
  transition: color 0.3s, border-color 0.3s, opacity 0.3s;
  background-color: #ffffff;

  ${(props) =>
    props.isActive &&
    css`
      color: #fb972e;
      border: 1px solid #fb972e !important;
    `}

  :hover {
    opacity: 0.7;
  }
`;

const TagRecipientWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-top: 20px;

  > div + div {
    margin-top: 16px;
  }
`;

const LogicButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: absolute;
  top: 23px;
`;

const AddCriteriaButton = styled.div`
  width: 100%;
  cursor: pointer;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0px;
  color: #fb972e;

  transition: color 0.3s;
  padding: 20px 0px;

  :hover {
    color: #fb972e80;
  }
`;

type Props = {
  search: SavedSearch;
  handlePage: (page: SavedSearchModal) => void;
  handleSavedSearch: (search: CountedSavedSearches) => void;
};

const NewRecipientTag = (): IRecipientTag => ({
  option: {
    name: "",
    value: "",
  },
  logic: { value: "AND", name: "And" },
  tags: [],
  id: uuidv4(),
});

export default function EditTagsSearchModal(props: Props): JSX.Element {
  const { search, handlePage, handleSavedSearch } = props;
  const tags = useRecoilValue(TagsAtom);
  const modal = useModal();
  const setCountedSavedSearches = useSetRecoilState(CountedSavedSearchesAtom);
  const [isLoading, setIsloading] = useState(false);

  const [recipientTags, setRecipientTags] = useState<IRecipientTag[]>(
    tagQueryToRecipient(search.search, tags)
  );

  async function addRecipientTags() {
    setRecipientTags([...recipientTags, NewRecipientTag()]);
  }

  async function handleSaveRecipient() {
    setIsloading(true);
    let invalidTag = false;
    let invalidOption = false;

    for (let index = 0; index < recipientTags.length; index++) {
      const element = recipientTags[index];

      if (element.option.value !== "ALL" && element.tags.length === 0) {
        invalidTag = true;
      }

      if (element.option.value === "") {
        invalidOption = true;
      }
    }

    if (invalidOption) {
      toast("Select a valid filter option.", { type: "warning" });
      setIsloading(false);

      return;
    }

    if (invalidTag) {
      toast("Select tags to filter.", { type: "warning" });
      setIsloading(false);

      return;
    }

    const tagQuery = recipientToTagQuery(recipientTags);

    const [contactsCount] = await ContactApi.countContactsByTagQueries([
      tagQuery,
    ]);

    const isSuccessUpdateSearch = await updateSavedSearch(search.id, {
      name: search.name,
      publicArchive: search.publicArchive,
      search: tagQuery,
    });

    if (isSuccessUpdateSearch) {
      setCountedSavedSearches((s) =>
        s.map((searchFromMap) => {
          if (searchFromMap.id === search.id)
            return { ...searchFromMap, search: tagQuery, contactsCount };
          return searchFromMap;
        })
      );

      handleSavedSearch({ ...search, search: tagQuery, contactsCount });

      toast("Saved search updated", { type: "success" });
    }
    setIsloading(false);
  }

  function handleLogicRecipientTags(logic: IOption, index: number) {
    const items = [...recipientTags];
    const item = { ...items[index] };
    item.logic = logic;
    items[index] = item;

    setRecipientTags(items);
  }

  function removeRecipientTags(currentTag: IRecipientTag) {
    if (recipientTags.length > 1) {
      setRecipientTags(
        recipientTags.filter((recipientTag) => {
          return recipientTag.id !== currentTag.id;
        })
      );
    } else {
      toast("Must have at least one recipient", { type: "warning" });
    }
  }

  const handleOptionRecipientTags = (option: IOption, index: number) => {
    const items = [...recipientTags];
    const item = { ...items[index] };
    item.option = { value: option.value, name: option.name };
    items[index] = item;

    setRecipientTags(items);
  };

  function handleTagsRecipientTags(tags: ITag[], index: number) {
    const items = [...recipientTags];
    const item = { ...items[index] };
    item.tags = tags;
    items[index] = item;
    setRecipientTags(items);
  }

  //If I have more than one tag criteria, then I go to the first one and change it
  //to "All Contacts", all the other ones goes bye bye
  useEffect(() => {
    if (recipientTags.length === 1) return;
    const firstOne = recipientTags[0];

    if (firstOne.option.value === "ALL") {
      setRecipientTags([firstOne]);
    }
  }, [recipientTags]);

  return (
    <Container>
      <Header>
        <Flex>
          <div
            css={css`
              cursor: pointer;
              height: 15px;
            `}
            onClick={() => {
              if (!isLoading) handlePage(SavedSearchModal.MANAGE);
            }}
          >
            <FaChevronLeft color="#BBBBBB" size={15} />
          </div>

          <HeaderTitle
            css={css`
              margin-left: 5px;
            `}
          >
            Edit Contacts
          </HeaderTitle>
        </Flex>

        <div
          css={css`
            cursor: pointer;
          `}
        >
          <IoMdClose
            color="#868482"
            size={25}
            onClick={() => modal.popModal()}
          />
        </div>
      </Header>
      <Wrapper>
        <TagRecipientWrapper>
          {recipientTags.map((recipientTag, index) => (
            <>
              <TagSelectRecipient
                key={`recipients_recipient_tag_${recipientTag.id}`}
                recipientTag={recipientTag}
                index={index}
                removeRecipientTags={removeRecipientTags}
                handleOptionRecipientTags={handleOptionRecipientTags}
                handleTagsRecipientTags={handleTagsRecipientTags}
              />
              {recipientTags[index + 1] && (
                <div
                  css={css`
                    position: relative;
                    margin: 0px -24px;
                    width: calc(100% + 50px);
                    border-bottom: 1px solid #eceeee;
                    height: 40px;
                    margin-top: 0px !important;
                    margin-bottom: 20px;
                  `}
                >
                  <LogicButtons>
                    <LogicButton
                      css={css`
                        border-radius: 4px 0px 0px 4px;
                        border-right: 1px solid #ffffff;
                      `}
                      isActive={recipientTag.logic.name === "And"}
                      onClick={() =>
                        handleLogicRecipientTags(
                          { name: "And", value: "AND" },
                          index
                        )
                      }
                    >
                      And
                    </LogicButton>
                    <LogicButton
                      css={css`
                        border-radius: 0px 4px 4px 0px;
                        border-left: 1px solid #ffffff;
                      `}
                      isActive={recipientTag.logic.name === "Or"}
                      onClick={() =>
                        handleLogicRecipientTags(
                          { name: "Or", value: "OR" },
                          index
                        )
                      }
                    >
                      Or
                    </LogicButton>
                  </LogicButtons>
                </div>
              )}
            </>
          ))}
        </TagRecipientWrapper>

        {recipientTags[0].option.value !== "ALL" && (
          <AddCriteriaButton onClick={addRecipientTags}>
            + Add criteria
          </AddCriteriaButton>
        )}
      </Wrapper>

      <Bottom>
        <StyledButton
          inverted
          onClick={() => modal.popModal()}
          style={{ marginRight: "10px", width: "92px" }}
        >
          Cancel
        </StyledButton>
        <StyledButton
          isLoading={isLoading}
          style={{ width: "92px" }}
          onClick={() => {
            handleSaveRecipient();
          }}
        >
          Save
        </StyledButton>
      </Bottom>
    </Container>
  );
}
